import { type CSSProperties } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select, {
  type OptionProps,
  type SingleValue,
  type SingleValueProps,
  type StylesConfig,
} from 'react-select';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpDescription, getHelpHtml } from '../../utils/help/help-content';
import type { SetupDomainOptionData } from './types';

const DOMAIN_SELECT_STYLES: StylesConfig<SetupDomainOptionData, false> = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  container: (base) => ({ ...base, margin: 0 }),
  control: (base, state) => ({
    ...base,
    direction: 'ltr',
    minHeight: '38px',
    alignItems: 'center',
    paddingLeft: '10px',
    boxShadow: 'none',
    borderColor: state.isFocused
      ? 'var(--falcon-input-focus-border-color)'
      : base.borderColor,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
    alignItems: 'center',
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    position: 'relative',
    top: 'auto',
    transform: 'none',
    maxWidth: '100%',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '38px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    direction: 'ltr',
    color: 'var(--falcon-body-color)',
  }),
};

function filterDomainOption(
  option: { label: string; value: string; data: SetupDomainOptionData },
  input: string,
) {
  const query = input.trim().toLowerCase();
  if (!query) return true;
  const haystack = [option.label, option.value, option.data.show_name ?? '']
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

type SetupDomainSelectProps = {
  options: SetupDomainOptionData[];
  value: SetupDomainOptionData | null;
  onChange: (option: SingleValue<SetupDomainOptionData>) => void;
  onAddDomain: () => void;
};

export function SetupDomainSelect({
  options,
  value,
  onChange,
  onAddDomain,
}: SetupDomainSelectProps) {
  return (
    <Form.Group className="mb-0 setup-domain-field">
      <style>{`
        .setup-domain-field .react-select__value-container--has-value {
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .setup-domain-field .setup-add-domain-btn {
          width: 38px;
          height: 38px;
          min-width: 38px;
          min-height: 38px;
          padding: 0 !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
        }
        .setup-domain-field .setup-add-domain-btn svg {
          display: block;
          margin: 0;
        }
      `}</style>
      <Form.Label className="fs--1 fw-medium d-inline-flex align-items-center mb-1" htmlFor="setup-domain-select">
        یک دامنه انتخاب کنید
        <HelpTooltip html={getHelpHtml('sections.setup_domain')} />
      </Form.Label>
      <div className="d-flex align-items-center gap-2">
        <div className="flex-grow-1 min-w-0">
          <Select<SetupDomainOptionData, false>
            classNamePrefix="react-select"
            inputId="setup-domain-select"
            options={options}
            value={value}
            onChange={onChange}
            isSearchable
            filterOption={filterDomainOption}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPlacement="auto"
            noOptionsMessage={() => 'دامنه‌ای یافت نشد'}
            placeholder="جستجو یا انتخاب دامنه"
            components={{
              Option: SetupDomainOption,
              SingleValue: SetupDomainValue,
            }}
            styles={DOMAIN_SELECT_STYLES}
          />
        </div>
        <ChildrensTooltip html={getHelpDescription('actions.add_domain')} placement="top" noChildSpan>
          <Button
            type="button"
            variant="outline-primary"
            className="setup-add-domain-btn flex-shrink-0"
            onClick={onAddDomain}
            aria-label="افزودن دامنه جدید"
          >
            <FontAwesomeIcon icon="plus" className="fs-0" />
          </Button>
        </ChildrensTooltip>
      </div>
    </Form.Group>
  );
}

function DomainOptionLabel({ domain, showName }: { domain: string; showName?: string }) {
  return (
    <>
      <DomainFavicon domain={domain} />
      {domain}
      {showName ? <span className="fs--2 text-500"> ({showName})</span> : null}
    </>
  );
}

function SetupDomainOption(props: OptionProps<SetupDomainOptionData>) {
  const { innerProps, innerRef, data, isSelected } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        direction: 'ltr',
        backgroundColor: isSelected
          ? 'var(--falcon-gray-link-hover-100)'
          : 'var(--falcon-dropdown-bg-global)',
      }}
      className="p-2 cursor-pointer border border-1"
    >
      <div className="fs-0 text-900 d-flex align-items-center gap-1">
        <DomainOptionLabel domain={data.label} showName={data.show_name} />
      </div>
    </div>
  );
}

function SetupDomainValue(props: SingleValueProps<SetupDomainOptionData>) {
  const { data, innerProps, getStyles } = props;
  return (
    <div
      {...innerProps}
      style={{
        ...(getStyles('singleValue', props) as CSSProperties),
        color: 'var(--falcon-body-color)',
        direction: 'ltr',
        display: 'flex',
        alignItems: 'center',
        gap: '0.25rem',
        position: 'relative',
        top: 'auto',
        transform: 'none',
        maxWidth: '100%',
      }}
    >
      <DomainOptionLabel domain={data.label} showName={data.show_name} />
    </div>
  );
}
